// Assessment-taking specific types
// These extend the base API types with more specific content structures

import type { Question, Answer, CodingLanguage } from "./api";
import type { HLDWizardContent, HLDWizardResponse } from "./hld-wizard";
export type { HLDWizardContent, HLDWizardResponse } from "./hld-wizard";
export { isHLDWizardContent, isHLDWizardUIAnswer, isHLDWizardHiddenAnswer } from "./hld-wizard";

// =============================================================================
// Question Content Types (what the API returns in question.content)
// =============================================================================

export interface QuestionOption {
  id: number;
  content: string;
  order: number;
}

export interface SingleChoiceContent {
  text: string;
  options: QuestionOption[];
}

export interface MultiSelectContent {
  text: string;
  options: QuestionOption[];
}

export interface OpenAnswerContent {
  text: string;
  context?: string;
  max_length?: number;
}

// =============================================================================
// Code-based Question Content Types
// =============================================================================

export interface CodeSnippet {
  filename: string;
  code: string;
}

export type ReviewCategory =
  | "security"
  | "performance"
  | "bug"
  | "style"
  | "maintainability"
  | "concurrency"
  | "design";

export interface ReviewComment {
  id: string; // Client-generated UUID for tracking
  filename?: string;
  start_line: number;
  end_line?: number;
  category?: ReviewCategory;
  description: string;
  suggestion?: string;
}

export interface CodeReviewContent {
  text?: string;
  snippets: Record<string, CodeSnippet[]>; // language -> files
}

export interface RefactorContent {
  text?: string;
  snippets: Record<string, CodeSnippet[]>; // language -> files
}

// =============================================================================
// HLD Question Content Types
// =============================================================================

export interface HLDRubricItem {
  section: string; // e.g., "Architecture", "Data Model"
  description: string; // What to look for in this section
  weight: number; // Importance (1-10)
}

export interface HLDContent {
  text: string; // Question/prompt explaining what to design
  template: string; // Unfinished markdown template to complete
  rubric: HLDRubricItem[]; // Evaluation criteria (visible to users)
}

// Union type for simple question content
export type SimpleQuestionContent =
  | SingleChoiceContent
  | MultiSelectContent
  | OpenAnswerContent
  | CodeReviewContent
  | RefactorContent
  | HLDContent
  | HLDWizardContent;

// =============================================================================
// Answer Types (what we submit to the API)
// =============================================================================

// Single choice: just the selected option ID (number for API)
export type SingleChoiceAnswer = number;

// Multi-select: array of selected option IDs (numbers for API)
export type MultiSelectAnswer = number[];

// Open answer: the text response
export type OpenAnswerAnswer = string;

// Code Review answer: language + array of comments
export interface CodeReviewAnswer {
  language: string;
  comments: Omit<ReviewComment, "id">[]; // API doesn't need client IDs
}

// Refactor answer: language + array of code snippets
export interface RefactorAnswer {
  language: string;
  code: CodeSnippet[];
}

// HLD answer: the completed markdown document
export type HLDAnswer = string;

// Union type for simple answers (what we send to API)
export type SimpleAnswer =
  | SingleChoiceAnswer
  | MultiSelectAnswer
  | OpenAnswerAnswer
  | CodeReviewAnswer
  | RefactorAnswer
  | HLDAnswer
  | HLDWizardResponse;

// =============================================================================
// UI Answer Types (what components use - strings for HTML compatibility)
// =============================================================================

export type SingleChoiceUIAnswer = string;
export type MultiSelectUIAnswer = string[];
export type OpenAnswerUIAnswer = string;

// Code review UI answer: language + comments with client-side IDs
export interface CodeReviewUIAnswer {
  language: string;
  comments: ReviewComment[];
}

// Refactor UI answer: language + edited code
export interface RefactorUIAnswer {
  language: string;
  code: CodeSnippet[];
}

// HLD UI answer: the completed markdown document (same as API answer)
export type HLDUIAnswer = string;

export type UIAnswer =
  | SingleChoiceUIAnswer
  | MultiSelectUIAnswer
  | OpenAnswerUIAnswer
  | CodeReviewUIAnswer
  | RefactorUIAnswer
  | HLDUIAnswer
  | HLDWizardResponse;

// =============================================================================
// Hidden Answer Types (returned by API after submission)
// =============================================================================

export interface SingleChoiceHiddenAnswer {
  correct_option_id: number;
  feedback?: string;
}

export interface MultiSelectOptionAnswer {
  option_id: number;
  is_correct: boolean;
  feedback?: string;
}

export interface MultiSelectHiddenAnswer {
  options: MultiSelectOptionAnswer[];
}

export interface OpenAnswerHiddenAnswer {
  correct_answer: string;
  context?: string;
}

export interface CodeReviewHiddenAnswer {
  expected_issues: Record<string, ReviewComment[]>; // language -> expected issues
}

export interface RefactorHiddenAnswer {
  expected_code: Record<string, CodeSnippet[]>; // language -> expected code
  evaluation_criteria: string[];
  acceptable_variations?: string;
}

export interface HLDHiddenAnswer {
  example_hld?: string; // Complete example solution for LLM reference
  context?: string; // Additional grading context for LLM
}

// Type guard functions for hidden answers
export function isSingleChoiceHiddenAnswer(
  hidden: unknown
): hidden is SingleChoiceHiddenAnswer {
  return (
    typeof hidden === "object" &&
    hidden !== null &&
    "correct_option_id" in hidden &&
    typeof (hidden as SingleChoiceHiddenAnswer).correct_option_id === "number"
  );
}

export function isMultiSelectHiddenAnswer(
  hidden: unknown
): hidden is MultiSelectHiddenAnswer {
  return (
    typeof hidden === "object" &&
    hidden !== null &&
    "options" in hidden &&
    Array.isArray((hidden as MultiSelectHiddenAnswer).options)
  );
}

export function isOpenAnswerHiddenAnswer(
  hidden: unknown
): hidden is OpenAnswerHiddenAnswer {
  return (
    typeof hidden === "object" &&
    hidden !== null &&
    "correct_answer" in hidden &&
    typeof (hidden as OpenAnswerHiddenAnswer).correct_answer === "string"
  );
}

export function isCodeReviewHiddenAnswer(
  hidden: unknown
): hidden is CodeReviewHiddenAnswer {
  return (
    typeof hidden === "object" &&
    hidden !== null &&
    "expected_issues" in hidden &&
    typeof (hidden as CodeReviewHiddenAnswer).expected_issues === "object"
  );
}

export function isRefactorHiddenAnswer(
  hidden: unknown
): hidden is RefactorHiddenAnswer {
  return (
    typeof hidden === "object" &&
    hidden !== null &&
    "expected_code" in hidden &&
    typeof (hidden as RefactorHiddenAnswer).expected_code === "object"
  );
}

export function isHLDHiddenAnswer(
  hidden: unknown
): hidden is HLDHiddenAnswer {
  // HLD hidden answer is optional fields only, so we just check it's an object
  // and doesn't match other hidden answer types
  return (
    typeof hidden === "object" &&
    hidden !== null &&
    !("correct_option_id" in hidden) &&
    !("options" in hidden) &&
    !("correct_answer" in hidden) &&
    !("expected_issues" in hidden) &&
    !("expected_code" in hidden)
  );
}

// =============================================================================
// Feedback Types (what we display after submission)
// =============================================================================

export interface FeedbackState {
  // Undefined in blind-mode assessments (server omits is_correct from the response).
  isCorrect?: boolean;
  score?: number;
  maxScore?: number;
  explanation?: string;
  // For choice questions, which options were correct
  correctOptions?: string[];
}

// =============================================================================
// Assessment State Types (for Zustand store)
// =============================================================================

export interface AnswerRecord {
  questionId: number;
  answer: UIAnswer;  // Store UI answer for display in results
  feedback: FeedbackState | null;
  submittedAt: Date;
}

export interface AssessmentState {
  // Current assessment/attempt info
  attemptId: number | null;
  assessmentId: number | null;
  assessmentTitle: string;
  questions: Question[];
  currentIndex: number;

  // Answer tracking (UIAnswer for component compatibility)
  currentAnswer: UIAnswer | null;
  isSubmitting: boolean;
  submittedAnswers: Record<number, AnswerRecord>;

  // Feedback
  feedback: FeedbackState | null;
  showFeedback: boolean;

  // Completion
  isCompleted: boolean;
  finalScore: number | null;

  // Review mode
  isReviewMode: boolean;
  hiddenAnswers: Record<number, Record<string, unknown>>;
  videoExplanations: Record<number, string>;

  // Drafts (restored on attempt resume)
  drafts: Record<number, unknown>;

  // Actions
  initAssessment: (
    attemptId: number,
    assessmentId: number,
    title: string,
    questions: Question[],
    existingAnswers?: Answer[],
    hiddenAnswers?: Record<number, Record<string, unknown>>,
    drafts?: Record<number, unknown>
  ) => void;
  initReviewMode: (
    attemptId: number,
    assessmentId: number,
    title: string,
    questions: Question[],
    answers: Answer[],
    hiddenAnswers: Record<number, Record<string, unknown>>,
    score: number
  ) => void;
  setCurrentAnswer: (answer: UIAnswer) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  setFeedback: (feedback: FeedbackState, answerRecord: AnswerRecord) => void;
  setHiddenAnswer: (questionId: number, hiddenAnswer: Record<string, unknown>) => void;
  setVideoExplanation: (questionId: number, url: string) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  goToQuestion: (index: number) => void;
  completeAssessment: (score: number) => void;
  reset: () => void;
}

// =============================================================================
// Helper Type Guards
// =============================================================================

function hasOptions(content: Record<string, unknown>): boolean {
  return (
    typeof content.text === "string" &&
    Array.isArray(content.options) &&
    content.options.every(
      (opt: unknown) =>
        typeof opt === "object" &&
        opt !== null &&
        "id" in opt &&
        "content" in opt
    )
  );
}

function hasText(content: Record<string, unknown>): boolean {
  return typeof content.text === "string";
}

function hasSnippets(content: Record<string, unknown>): boolean {
  return (
    typeof content.snippets === "object" &&
    content.snippets !== null &&
    Object.keys(content.snippets as object).length > 0
  );
}

function hasTemplateAndRubric(content: Record<string, unknown>): boolean {
  return (
    typeof content.text === "string" &&
    typeof content.template === "string" &&
    Array.isArray(content.rubric)
  );
}

// =============================================================================
// Answer Conversion (UI strings ↔ API numbers)
// =============================================================================

/**
 * Convert UI answer format to API format with runtime validation (fix 3.1)
 */
export function convertAnswerForAPI(
  questionType: string,
  uiAnswer: UIAnswer
): SimpleAnswer {
  switch (questionType) {
    case "single_choice": {
      if (typeof uiAnswer !== "string") {
        throw new Error(
          `Expected string for single_choice, got ${typeof uiAnswer}`
        );
      }
      const parsed = parseInt(uiAnswer, 10);
      if (isNaN(parsed)) {
        throw new Error(`Invalid option ID for single_choice: ${uiAnswer}`);
      }
      return parsed;
    }
    case "multi_select": {
      if (!Array.isArray(uiAnswer)) {
        throw new Error(
          `Expected array for multi_select, got ${typeof uiAnswer}`
        );
      }
      return uiAnswer.map((id) => {
        if (typeof id !== "string") {
          throw new Error(`Invalid option ID type in multi_select: ${typeof id}`);
        }
        const parsed = parseInt(id, 10);
        if (isNaN(parsed)) {
          throw new Error(`Invalid option ID in multi_select: ${id}`);
        }
        return parsed;
      });
    }
    case "code_review": {
      if (
        typeof uiAnswer !== "object" ||
        uiAnswer === null ||
        !("comments" in uiAnswer)
      ) {
        throw new Error("Invalid code_review answer format");
      }
      const codeReview = uiAnswer as CodeReviewUIAnswer;
      return {
        language: codeReview.language,
        comments: codeReview.comments.map(({ id, ...rest }) => rest),
      };
    }
    case "refactor": {
      if (
        typeof uiAnswer !== "object" ||
        uiAnswer === null ||
        !("code" in uiAnswer)
      ) {
        throw new Error("Invalid refactor answer format");
      }
      return uiAnswer as RefactorUIAnswer;
    }
    case "hld": {
      if (typeof uiAnswer !== "string") {
        throw new Error(`Expected string for hld, got ${typeof uiAnswer}`);
      }
      return uiAnswer;
    }
    case "hld_wizard": {
      return uiAnswer as HLDWizardResponse;
    }
    case "open_answer":
    default: {
      if (typeof uiAnswer !== "string") {
        throw new Error(
          `Expected string for open_answer, got ${typeof uiAnswer}`
        );
      }
      return uiAnswer;
    }
  }
}

export function convertAnswerForUI(
  questionType: string,
  apiAnswer: unknown
): UIAnswer {
  switch (questionType) {
    case "single_choice":
      // Convert number to string
      return String(apiAnswer as number);
    case "multi_select":
      // Convert number[] to string[]
      return (apiAnswer as number[]).map((id) => String(id));
    case "code_review": {
      // Add client-side IDs to comments for tracking
      const answer = apiAnswer as CodeReviewAnswer;
      return {
        language: answer.language,
        comments: answer.comments.map((c, i) => ({
          ...c,
          id: `restored-${i}`,
        })),
      };
    }
    case "refactor":
      // Already in correct format
      return apiAnswer as RefactorUIAnswer;
    case "hld":
      // Already a string
      return apiAnswer as string;
    case "hld_wizard":
      return apiAnswer as HLDWizardResponse;
    case "open_answer":
    default:
      // Already a string
      return apiAnswer as string;
  }
}

// =============================================================================
// Question Type to Content Type mapping
// =============================================================================

export function getQuestionContent(question: Question): SimpleQuestionContent {
  const content = question.content;

  switch (question.type) {
    case "single_choice":
      if (hasOptions(content)) return content as unknown as SingleChoiceContent;
      break;
    case "multi_select":
      if (hasOptions(content)) return content as unknown as MultiSelectContent;
      break;
    case "open_answer":
      if (hasText(content)) return content as unknown as OpenAnswerContent;
      break;
    case "code_review":
      if (hasSnippets(content)) return content as unknown as CodeReviewContent;
      break;
    case "refactor":
      if (hasSnippets(content)) return content as unknown as RefactorContent;
      break;
    case "hld":
      if (hasTemplateAndRubric(content)) return content as unknown as HLDContent;
      break;
    case "hld_wizard":
      if ("scenario" in content && "decisions" in content)
        return content as unknown as HLDWizardContent;
      break;
  }

  // Fallback for unexpected content
  return {
    text: "Unable to load question content",
    options: [],
  };
}

// =============================================================================
// Code Question Type Helpers
// =============================================================================

/**
 * Get the initial language for a code-based question.
 * Uses user's default if available, otherwise falls back to first available.
 */
export function getInitialLanguage(
  availableLanguages: string[],
  userDefaultLanguage?: string
): string {
  if (
    userDefaultLanguage &&
    availableLanguages.includes(userDefaultLanguage)
  ) {
    return userDefaultLanguage;
  }
  return availableLanguages[0] || "go";
}

/**
 * Type guard for CodeReviewUIAnswer
 */
export function isCodeReviewUIAnswer(
  answer: UIAnswer | null
): answer is CodeReviewUIAnswer {
  return (
    answer !== null &&
    typeof answer === "object" &&
    "language" in answer &&
    "comments" in answer
  );
}

/**
 * Type guard for RefactorUIAnswer
 */
export function isRefactorUIAnswer(
  answer: UIAnswer | null
): answer is RefactorUIAnswer {
  return (
    answer !== null &&
    typeof answer === "object" &&
    "language" in answer &&
    "code" in answer
  );
}

/**
 * Type guard for HLDUIAnswer
 */
export function isHLDUIAnswer(
  answer: UIAnswer | null
): answer is HLDUIAnswer {
  return typeof answer === "string";
}
